import { useState, useCallback, useRef } from 'react'
import { Image } from '../../types/image'
import { getThumbnailUrl } from '../../lib/images'
import { Check } from 'lucide-react'

interface GalleryItemProps {
  image: Image
  onClick?: () => void
  aspectRatio?: number
  isSelected?: boolean
  onToggleSelect?: () => void
  selectionMode?: boolean
}

export const GalleryItem: React.FC<GalleryItemProps> = ({
  image,
  onClick,
  aspectRatio,
  isSelected = false,
  onToggleSelect,
  selectionMode = false,
}) => {
  const [isLoaded, setIsLoaded] = useState(false)
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const imageUrl = getThumbnailUrl(image, 'medium')
  const hasValidAspectRatio = aspectRatio !== undefined && aspectRatio > 0 && Number.isFinite(aspectRatio)

  const handleCheckboxClick = useCallback(
    (e: React.MouseEvent | React.KeyboardEvent) => {
      e.stopPropagation()
      e.preventDefault()
      onToggleSelect?.()
    },
    [onToggleSelect],
  )

  const handleCheckboxKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        e.stopPropagation()
        onToggleSelect?.()
      }
    },
    [onToggleSelect],
  )

  const handleTileClick = useCallback(() => {
    // In selection mode, clicking the tile toggles selection instead of opening the lightbox
    if (selectionMode) {
      onToggleSelect?.()
    } else {
      onClick?.()
    }
  }, [selectionMode, onToggleSelect, onClick])

  const handleTileKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        handleTileClick()
      }
    },
    [handleTileClick],
  )

  // Long-press to select on mobile
  const handleTouchStart = useCallback(() => {
    longPressTimer.current = setTimeout(() => {
      onToggleSelect?.()
    }, 500)
  }, [onToggleSelect])

  const handleTouchEnd = useCallback(() => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current)
      longPressTimer.current = null
    }
  }, [])

  const handleTouchMove = useCallback(() => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current)
      longPressTimer.current = null
    }
  }, [])

  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={isSelected ? `${image.filename} (selected)` : `View ${image.filename}`}
      aria-selected={isSelected}
      className={`relative mb-3 group cursor-pointer overflow-hidden rounded-lg bg-gray-100 transition-all duration-200 ${
        isSelected
          ? 'ring-2 ring-blue-500 shadow-lg shadow-blue-500/20 scale-[1.02]'
          : 'hover:shadow-lg group-hover:shadow-lg'
      } ${hasValidAspectRatio ? '' : 'aspect-auto'}`}
      style={hasValidAspectRatio ? { aspectRatio: String(aspectRatio) } : undefined}
      onClick={handleTileClick}
      onKeyDown={handleTileKeyDown}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onTouchMove={handleTouchMove}
    >
      <img
        src={imageUrl}
        alt={image.filename}
        className={`w-full h-auto transition-opacity duration-300 ${
          isLoaded ? 'opacity-100' : 'opacity-0'
        }`}
        loading="lazy"
        onLoad={() => setIsLoaded(true)}
      />

      {/* Selection checkbox - always dimly visible, full opacity when selected/hovered/in selection mode */}
      <div
        className={`absolute top-2 left-2 z-10 transition-all duration-200 ${
          isSelected || selectionMode
            ? 'opacity-100'
            : 'opacity-30 group-hover:opacity-100 focus-within:opacity-100'
        }`}
      >
        <button
          onClick={handleCheckboxClick}
          onKeyDown={handleCheckboxKeyDown}
          className={`w-8 h-8 sm:w-7 sm:h-7 rounded-full flex items-center justify-center transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 touch-manipulation ${
            isSelected
              ? 'bg-blue-500 text-white shadow-md'
              : 'bg-white/80 text-gray-700 hover:bg-white hover:text-gray-900'
          }`}
          aria-label={isSelected ? `Deselect ${image.filename}` : `Select ${image.filename}`}
          aria-checked={isSelected}
          role="checkbox"
          tabIndex={0}
        >
          <Check className={`w-5 h-5 sm:w-4 sm:h-4 ${isSelected ? 'opacity-100' : 'opacity-60 group-hover:opacity-100'}`} />
        </button>
      </div>

      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300">
        <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          {image.exifData?.camera && (
            <p className="text-white/90 text-xs font-medium truncate">
              {image.exifData.camera}
            </p>
          )}
        </div>
      </div>

      {image.status === 'processing' && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/50">
          <div className="text-white text-sm">Processing...</div>
        </div>
      )}
      {image.status === 'error' && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/50">
          <div className="text-white text-sm">Error</div>
        </div>
      )}
    </div>
  )
}
